import React, { useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import api from '../../../../services/api';

const STATUS_LABEL = {
    draft: { label: 'Rascunho', color: 'default' },
    sent: { label: 'Enviado', color: 'warning' },
    accepted: { label: 'Aceito', color: 'success' },
    rejected: { label: 'Rejeitado', color: 'error' },
    expired: { label: 'Expirado', color: 'default' }
};

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const ViewSalesQuote = () => {
    const { id } = useParams();
    const history = useHistory();

    const [appId, setAppId] = useState(null);
    const [quote, setQuote] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [working, setWorking] = useState(false);

    // Protege contra setState depois de desmontar - ações como "Converter
    // em Pedido" navegam pra outra rota assim que a requisição termina, e
    // o `finally`/catch ainda pode rodar depois do componente já ter saído
    // de tela.
    const mountedRef = useRef(true);
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            if (mountedRef.current) setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId || !id || id === 'new') return;
        carregarOrcamento();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, id]);

    const carregarOrcamento = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/sales_quotes/${id}/`);
            if (mountedRef.current) setQuote(res.data);
        } catch (error) {
            console.error(error);
            if (mountedRef.current) setStatus({ type: 'error', message: 'Erro ao carregar orçamento' });
        }
    };

    const runAction = async (action, successMessage) => {
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/sales_quotes/${id}/${action}/`, {});
            if (mountedRef.current) {
                setQuote(res.data);
                setStatus({ type: 'success', message: successMessage });
            }
        } catch (error) {
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao executar ação' });
        } finally {
            if (mountedRef.current) setWorking(false);
        }
    };

    const handleConvert = async () => {
        if (!window.confirm('Converter este orçamento em pedido de venda?')) return;
        setWorking(true);
        setStatus({ type: '', message: '' });
        try {
            const res = await api.post(`v1/apps/${appId}/sales_quotes/${id}/convert-to-order/`, {});
            history.push(`/dashboard/sales/${res.data.id}/`);
        } catch (error) {
            if (mountedRef.current) setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao converter orçamento' });
        } finally {
            if (mountedRef.current) setWorking(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('Excluir este orçamento em rascunho?')) return;
        setWorking(true);
        try {
            await api.delete(`v1/apps/${appId}/sales_quotes/${id}/`);
            history.push('/dashboard/sales/quotes');
        } catch (error) {
            if (mountedRef.current) {
                setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao excluir orçamento' });
                setWorking(false);
            }
        }
    };

    if (!quote) {
        return <Box>{status.message && <Alert severity={status.type}>{status.message}</Alert>}</Box>;
    }

    const statusInfo = STATUS_LABEL[quote.status] || { label: quote.status, color: 'default' };

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h3">Orçamento</Typography>
                <Chip label={statusInfo.label} color={statusInfo.color} />
            </Stack>

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }}>
                    {status.message}
                </Alert>
            )}

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Cliente
                            </Typography>
                            <Typography>{quote.organization_name}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Data
                            </Typography>
                            <Typography>{quote.quote_date}</Typography>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <Typography variant="caption" color="text.secondary">
                                Válido até
                            </Typography>
                            <Typography>{quote.valid_until || '-'}</Typography>
                        </Grid>
                        {quote.notes && (
                            <Grid item xs={12}>
                                <Typography variant="caption" color="text.secondary">
                                    Observações
                                </Typography>
                                <Typography>{quote.notes}</Typography>
                            </Grid>
                        )}
                    </Grid>
                </CardContent>
            </Card>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Quantidade</TableCell>
                                <TableCell align="right">Valor unitário</TableCell>
                                <TableCell align="right">Subtotal</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {quote.items?.map((item) => (
                                <TableRow key={item.id}>
                                    <TableCell>{item.produto_variacao_nome || item.service_nome}</TableCell>
                                    <TableCell align="right">{item.quantidade}</TableCell>
                                    <TableCell align="right">{formatCurrencyBR(item.valor_unitario)}</TableCell>
                                    <TableCell align="right">{formatCurrencyBR(item.subtotal)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Box textAlign="right" mt={2}>
                        <Typography variant="h5">Total: {formatCurrencyBR(quote.total_amount)}</Typography>
                    </Box>
                </CardContent>
            </Card>

            <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={() => history.push('/dashboard/sales/quotes')}>Voltar</Button>

                {quote.status === 'draft' && (
                    <>
                        <Button color="error" variant="outlined" disabled={working} onClick={handleDelete}>
                            Excluir
                        </Button>
                        <Button variant="contained" disabled={working} onClick={() => runAction('send', 'Orçamento enviado ao cliente.')}>
                            Enviar ao Cliente
                        </Button>
                    </>
                )}

                {quote.status === 'sent' && (
                    <>
                        <Button color="error" variant="outlined" disabled={working} onClick={() => runAction('reject', 'Orçamento rejeitado.')}>
                            Rejeitar
                        </Button>
                        <Button variant="contained" disabled={working} onClick={() => runAction('accept', 'Orçamento aceito.')}>
                            Aceitar
                        </Button>
                    </>
                )}

                {(quote.status === 'draft' || quote.status === 'sent') && (
                    <Button variant="outlined" disabled={working} onClick={() => runAction('expire', 'Orçamento marcado como expirado.')}>
                        Marcar como Expirado
                    </Button>
                )}

                {quote.status === 'accepted' && !quote.converted && (
                    <Button variant="contained" disabled={working} onClick={handleConvert}>
                        Converter em Pedido
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default ViewSalesQuote;
